/**
 * Layer 1.5 - vertical slices: were notes meant to sound together actually together?
 *
 * Membership comes from the realized score: notes sharing a notated onset form an onset slice,
 * across hands; notes sharing a notated release form a release slice. Onset slices are on by
 * default; release slices are off by default (release spread is far less audible, means nothing
 * under the sustain pedal, and cannot be detected on every input). Successive legato notes are
 * never a slice.
 *
 * A slice is judged by a presence rule (each note independent, all present, or N of M) and,
 * for togetherness, a sync curve over the spread between its first and last member. The sync
 * curve is not redundant with per-note timing: it catches a chord whose notes each pass their
 * own window but land smeared, and forgives a chord that is late but together. It reads only
 * relative press times, so it works under order matching too. A rolled (arpeggiated) chord is
 * judged on its total spread and, optionally, on being played in pitch order.
 */

import { CreditCurve, creditAt, creditCurveErrors } from './creditCurve';
import { NoteJudgment, SliceJudgment, SliceKind } from './judgedTake';
import { Hand, ScoreNote } from './types';

export type PresenceRule =
  | { readonly kind: 'independent' }
  | { readonly kind: 'all' }
  | { readonly kind: 'n-of-m'; readonly n: number };

export interface RolledRule {
  /** Credit over the chord's total spread (first to last member). */
  readonly spread?: CreditCurve;
  /** When true, the members must be played from the lowest pitch up. */
  readonly ordered: boolean;
}

export interface SliceRule {
  readonly presence: PresenceRule;
  /** Togetherness for simultaneous slices: credit over the spread, ms or beats. */
  readonly sync?: CreditCurve;
  /** How rolled slices are judged (onset slices only). */
  readonly rolled?: RolledRule;
}

export interface SliceConfig {
  /** Onset slices; when absent they are formed but judged `independent` (no slice result). */
  readonly onset?: SliceRule;
  /** Release slices; off when absent. */
  readonly release?: SliceRule;
}

export const INDEPENDENT_SLICES: SliceRule = { presence: { kind: 'independent' } };

export function sliceConfigErrors(config: SliceConfig): string[] {
  const rules: [string, SliceRule | undefined][] = [
    ['onset slices', config.onset],
    ['release slices', config.release],
  ];
  return rules.reduce<string[]>((errors, [label, rule]) => {
    if (rule === undefined) {
      return errors;
    }
    const curves: [string, CreditCurve | undefined][] = [
      ['sync', rule.sync],
      ['rolled spread', rule.rolled?.spread],
    ];
    const curveErrors = curves.reduce<string[]>((found, [name, curve]) => {
      if (curve === undefined) {
        return found;
      }
      const unitErrors =
        curve.unit === 'ms' || curve.unit === 'beats'
          ? []
          : [`${label} ${name}: unit must be ms or beats`];
      const sideErrors =
        curve.sides === undefined
          ? []
          : [`${label} ${name}: per-side settings are for note dimensions only`];
      return [
        ...found,
        ...unitErrors,
        ...sideErrors,
        ...creditCurveErrors(curve).map((error) => `${label} ${name}: ${error}`),
      ];
    }, []);
    const presenceErrors =
      rule.presence.kind === 'n-of-m' && !(rule.presence.n >= 1)
        ? [`${label}: n-of-m needs n >= 1`]
        : [];
    const rolledErrors =
      label === 'release slices' && rule.rolled ? ['release slices cannot be rolled'] : [];
    return [...errors, ...curveErrors, ...presenceErrors, ...rolledErrors];
  }, []);
}

const BEAT_KEY_PRECISION = 1e6;

function membershipKey(note: ScoreNote, kind: SliceKind): string {
  if (kind === 'onset') {
    return note.onsetSlice ?? `beat:${Math.round(note.onsetBeat * BEAT_KEY_PRECISION)}`;
  }
  return note.releaseSlice ?? `beat:${Math.round(note.offsetBeat * BEAT_KEY_PRECISION)}`;
}

/** Groups notes into slices of two or more members, in score order. */
export function sliceMembership(notes: readonly ScoreNote[], kind: SliceKind): ScoreNote[][] {
  const groups = new Map<string, ScoreNote[]>();
  notes.forEach((note) => {
    const key = membershipKey(note, kind);
    groups.set(key, [...(groups.get(key) ?? []), note]);
  });
  return Array.from(groups.values()).filter((members) => members.length >= 2);
}

function inUnit(curve: CreditCurve, spreadMs: number, msPerBeat: number | null): number | null {
  if (curve.unit === 'ms') {
    return spreadMs;
  }
  return msPerBeat === null ? null : spreadMs / msPerBeat;
}

function presencePasses(rule: PresenceRule, present: number, total: number): boolean | null {
  if (rule.kind === 'independent') {
    return null;
  }
  return rule.kind === 'all' ? present === total : present >= Math.min(rule.n, total);
}

function uniqueHands(members: readonly NoteJudgment[]): Hand[] {
  return members.reduce<Hand[]>(
    (hands, member) => (hands.indexOf(member.hand) >= 0 ? hands : [...hands, member.hand]),
    [],
  );
}

export interface SliceContext {
  /** Milliseconds per beat at the slice, for beat-unit curves; null when there is no tempo. */
  readonly msPerBeat: (beat: number) => number | null;
}

/** Judges one slice from its members' note judgments. */
export function judgeSlice(
  kind: SliceKind,
  notes: readonly ScoreNote[],
  judgmentsById: ReadonlyMap<string, NoteJudgment>,
  rule: SliceRule,
  context: SliceContext,
): SliceJudgment {
  const members = notes
    .map((note) => judgmentsById.get(note.id))
    .filter((member): member is NoteJudgment => member !== undefined);
  const rolled = kind === 'onset' && notes.some((note) => note.rolled === true);
  const reached = members.some(
    (member) => member.status === 'matched' || member.status === 'missed',
  );
  const presentMembers = members.filter(
    (member) =>
      member.status === 'matched' &&
      (member.dimensions.pitch?.state !== 'judged' || member.pitchCorrect === true),
  );
  const times = presentMembers
    .map((member) => (kind === 'onset' ? member.press?.timeMs : member.releaseMs))
    .filter((time): time is number => typeof time === 'number');
  const underPedal =
    kind === 'release' && presentMembers.some((member) => member.releasedUnderSustain);
  const spreadMs =
    times.length >= 2 && !underPedal
      ? Math.max(...times) - Math.min(...times)
      : times.length === 1
        ? 0
        : null;
  const presencePassed = presencePasses(rule.presence, presentMembers.length, members.length);
  const msPerBeat = context.msPerBeat(notes[0].onsetBeat);
  const timingCurve = rolled ? rule.rolled?.spread : rule.sync;
  const timingInUnit =
    timingCurve && spreadMs !== null ? inUnit(timingCurve, spreadMs, msPerBeat) : null;
  const syncCredit =
    timingCurve && timingInUnit !== null ? creditAt(timingCurve, timingInUnit) : null;
  const playedInOrder = [...presentMembers]
    .sort((a, b) => (a.press?.timeMs ?? 0) - (b.press?.timeMs ?? 0))
    .every(
      (member, position, ordered) =>
        position === 0 || (member.press?.pitch ?? 0) >= (ordered[position - 1].press?.pitch ?? 0),
    );
  const inOrder = rolled && rule.rolled?.ordered ? playedInOrder : null;
  const settled =
    members.every((member) => member.settled) &&
    (kind === 'onset' || presentMembers.every((member) => member.releaseMs !== null));
  const credit =
    presencePassed === null || !reached || underPedal
      ? null
      : !presencePassed || inOrder === false
        ? 0
        : (syncCredit ?? 1);
  return {
    id: `${kind}:${notes[0].id}`,
    kind,
    memberIds: notes.map((note) => note.id),
    hands: uniqueHands(members),
    mechanic: rolled ? 'rolled' : 'simultaneous',
    present: presentMembers.length,
    total: members.length,
    presencePassed,
    spreadMs,
    syncCredit,
    inOrder,
    credit,
    reached,
    settled,
  };
}

/** Judges every slice of one kind among the graded notes. */
export function judgeSlices(
  kind: SliceKind,
  gradedNotes: readonly ScoreNote[],
  judgments: readonly NoteJudgment[],
  rule: SliceRule,
  context: SliceContext,
): SliceJudgment[] {
  const judgmentsById = new Map(judgments.map((judgment) => [judgment.noteId, judgment] as const));
  return sliceMembership(gradedNotes, kind).map((members) =>
    judgeSlice(kind, members, judgmentsById, rule, context),
  );
}
